import React, { useEffect, useState } from "react";
import { AlertCircle, Clock, ArrowRight, X } from "lucide-react";
import dayjs from "dayjs";

const IncompletePaymentStrip = ({
  notificationReleasedDate,
  accessRevokeDate,
}) => {
  const [open, setOpen] = useState(true);
  const [modalState, setModalState] = useState(
    localStorage.getItem("modalState") === "false" ? false : true
  );

  // Calculate precise deadline as 48 hours from the release date
  const revokeDate = dayjs(notificationReleasedDate).add(48, "hour");
  const [timeLeft, setTimeLeft] = useState({
    hours: "00",
    minutes: "00",
    seconds: "00",
    totalSeconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = dayjs();
      const diffInSeconds = revokeDate.diff(now, "second");

      if (diffInSeconds > 0) {
        const h = Math.floor(diffInSeconds / 3600);
        const m = Math.floor((diffInSeconds % 3600) / 60);
        const s = diffInSeconds % 60;

        setTimeLeft({
          hours: h.toString().padStart(2, "0"),
          minutes: m.toString().padStart(2, "0"),
          seconds: s.toString().padStart(2, "0"),
          totalSeconds: diffInSeconds
        });
      } else {
        setTimeLeft({ hours: "00", minutes: "00", seconds: "00", totalSeconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [accessRevokeDate]);

  const daysLeft = revokeDate.diff(dayjs(), "day");
  const formattedDeadline = revokeDate.format("DD MMMM YYYY");

  if (!open || !modalState) return null;

  const handleDismiss = () => {
    localStorage.setItem("modalState", false);
    setModalState(false);
    setOpen(false);
  };

  const isExpired = daysLeft < 0;
  const isUrgent = daysLeft <= 3;

  const getTheme = () => {
    if (isExpired) {
      return {
        bg: "#fef2f2", // red-50
        border: "#fee2e2", // red-100
        text: "#334155", // slate-700
        highlight: "#dc2626", // red-600
        iconBg: "#fee2e2", // red-100
        badgeBg: "#dc2626", // red-600
        badgeText: "#ffffff",
      };
    }
    if (isUrgent) {
      return {
        bg: "#fff1f2", // rose-50
        border: "#ffe4e6", // rose-100
        text: "#334155",
        highlight: "#e11d48", // rose-600
        iconBg: "#ffe4e6", // rose-100
        badgeBg: "#e11d48", // rose-600
        badgeText: "#ffffff",
      };
    }
    return {
      bg: "#eff6ff", // blue-50
      border: "#dbeafe", // blue-100
      text: "#334155",
      highlight: "#2563eb", // blue-600
      iconBg: "#dbeafe", // blue-100
      badgeBg: "#2563eb", // blue-600
      badgeText: "#ffffff",
    };
  };

  const theme = getTheme();

  return (
    <div
      className="incomplete-payment-strip"
      style={{
        width: "100%",
        background: theme.bg,
        color: theme.text,
        position: "relative",
        zIndex: 1301,
        fontFamily: "'Inter', 'Poppins', sans-serif",
        borderBottom: `1px solid ${theme.border}`,
      }}
    >
      <div className="payment-strip-container" style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "8px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
      }}>
        <div className="content-left" style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flex: 1,
        }}>
          <div className="icon-box" style={{
            background: theme.iconBg,
            borderRadius: "50%",
            width: "30px",
            height: "30px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            animation: isUrgent || isExpired ? "pulse-soft 2s infinite ease-in-out" : "none",
          }}>
            {isExpired ? <AlertCircle size={16} strokeWidth={2.5} color={theme.highlight} /> : <Clock size={16} strokeWidth={2.5} color={theme.highlight} />}
          </div>

          <div className="text-box" style={{ display: "flex", flexDirection: "row", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <span className="blinking-text" style={{ fontWeight: 700, fontSize: "0.85rem", color: theme.highlight }}>
              {isExpired ? "Access Restriction Alert:" : "Action Required:"}
            </span>
            <span className="description-text" style={{
              fontSize: "0.85rem",
              lineHeight: 1.4,
            }}>
              {isExpired ? (
                <>Your dashboard access is partially revoked. Please pay the remaining amount to get the access back.</>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                  <span>Kindly clear your pending program fee to avoid any restriction in your dashboard access. Time remaining:</span>
                  <div className="stopwatch" style={{
                    display: "flex",
                    gap: "4px",
                    alignItems: "center",
                    background: "#ffffff",
                    padding: "4px 8px",
                    borderRadius: "8px",
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.05)",
                    border: `1px solid ${theme.highlight}20`
                  }}>
                    <div className="time-unit">
                      <span style={{ fontWeight: 800, color: theme.highlight, fontSize: "0.9rem", fontFamily: "monospace" }}>{timeLeft.hours}</span>
                      <span style={{ fontSize: "0.6rem", textTransform: "uppercase", fontWeight: 700, opacity: 0.6, marginLeft: "2px" }}>H</span>
                    </div>
                    <span style={{ color: theme.highlight, fontWeight: 800 }}>:</span>
                    <div className="time-unit">
                      <span style={{ fontWeight: 800, color: theme.highlight, fontSize: "0.9rem", fontFamily: "monospace" }}>{timeLeft.minutes}</span>
                      <span style={{ fontSize: "0.6rem", textTransform: "uppercase", fontWeight: 700, opacity: 0.6, marginLeft: "2px" }}>M</span>
                    </div>
                    <span style={{ color: theme.highlight, fontWeight: 800 }}>:</span>
                    <div className="time-unit">
                      <span style={{ fontWeight: 800, color: theme.highlight, fontSize: "0.9rem", fontFamily: "monospace" }}>{timeLeft.seconds}</span>
                      <span style={{ fontSize: "0.6rem", textTransform: "uppercase", fontWeight: 700, opacity: 0.6, marginLeft: "2px" }}>S</span>
                    </div>
                  </div>
                </div>
              )}
            </span>

          </div>
        </div>

        <div className="actions-right" style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          flexShrink: 0,
        }}>
          <a
            href="/Duefee"
            className="pay-now-button"
            style={{
              background: theme.highlight,
              color: "#ffffff",
              padding: "6px 20px",
              borderRadius: "6px",
              fontSize: "0.8rem",
              fontWeight: 700,
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.3s ease",
              boxShadow: `0 4px 12px ${theme.highlight}40`,
              whiteSpace: "nowrap",
              animation: "button-pulse 2s infinite ease-in-out",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "scale(1.05)";
              e.currentTarget.style.boxShadow = `0 6px 15px ${theme.highlight}60`;
              e.currentTarget.style.filter = "brightness(1.1)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = `0 4px 12px ${theme.highlight}40`;
              e.currentTarget.style.filter = "brightness(1)";
            }}
          >
            Pay Now <ArrowRight size={14} strokeWidth={2.5} />
          </a>

          <button
            onClick={handleDismiss}
            className="dismiss-btn"
            style={{
              background: "transparent",
              border: "none",
              color: "#64748b",
              cursor: "pointer",
              padding: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              transition: "all 0.2s ease"
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = theme.iconBg; e.currentTarget.style.color = theme.highlight; }}
            onMouseOut={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#64748b"; }}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes pulse-soft {
            0% { transform: scale(1); box-shadow: 0 0 0 0 ${theme.highlight}40; }
            70% { transform: scale(1.1); box-shadow: 0 0 0 10px ${theme.highlight}00; }
            100% { transform: scale(1); box-shadow: 0 0 0 0 ${theme.highlight}00; }
          }

          @keyframes button-pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.03); }
            100% { transform: scale(1); }
          }

          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }

          .blinking-text {
            animation: blink 1.2s ease-in-out infinite;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .incomplete-payment-strip {
            background: ${theme.bg} !important;
            border-left: 6px solid ${theme.highlight} !important;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
          }

          .pay-now-button {
            box-shadow: 0 0 15px ${theme.highlight}40 !important;
          }

          .description-text {
            font-weight: 500 !important;
            color: #1e293b !important;
          }
          
          @media (max-width: 768px) {
            .payment-strip-container {
              flex-direction: column;
              align-items: flex-start !important;
              padding: 12px 20px !important;
              gap: 12px !important;
            }
            .content-left {
              width: 100%;
              align-items: flex-start !important;
            }
            .icon-box {
              margin-top: 2px;
            }
            .actions-right {
              width: 100%;
              justify-content: space-between;
              padding-left: 42px; /* Aligns with text */
            }
            .pay-now-button {
              flex: 1;
              justify-content: center;
            }
          }
        `}
      </style>
    </div>
  );
};

export default IncompletePaymentStrip;
